import React, { useMemo } from 'react';

import { Badge, Box, Divider, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { formatDiscountValue } from 'utils/discount.helpers';

import { useChangeModal } from '../../ChangeModalContext';

const ReviewRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Group position='apart' noWrap align='flex-start'>
    <Text size='sm' color='dimmed' sx={{ flexShrink: 0 }}>
      {label}
    </Text>
    <Text size='sm' fw={500} align='right'>
      {value}
    </Text>
  </Group>
);

const ReviewStep: React.FC = () => {
  const {
    form,
    athletePackage,
    upcomingCreditedCount,
    upcomingUncreditedCount,
    upcomingTotalCount,
    errorMessage,
  } = useChangeModal();
  const { formatCurrency } = useLocalization();

  const { timing, reservationChoice, captured } = form.values;

  const whenChangesApply = useMemo(() => {
    if (timing === 'TOMORROW') {
      return `Tomorrow (${dayjs().add(1, 'day').format('MMM D, YYYY')})`;
    }
    if (timing === 'WHEN_ENDS' && athletePackage.endDate) {
      return `When current package ends (${dayjs(athletePackage.endDate)
        .add(1, 'day')
        .format('MMM D, YYYY')})`;
    }
    return '—';
  }, [timing, athletePackage.endDate]);

  const willKeep = reservationChoice === 'KEEP';
  const willDelete = reservationChoice === 'DELETE';

  const existingChild = athletePackage.child;
  const existingChildKind = existingChild?.isChange ? 'scheduled change' : 'renewal';

  const hasReservationWarnings =
    (willKeep && upcomingTotalCount > 0) || (willDelete && upcomingTotalCount > 0);

  const effectiveAutoRenew = captured?.followUpPackageId ? false : captured?.autoRenew ?? false;

  return (
    <Stack spacing='md'>
      <Paper p='md' radius='md' withBorder>
        <Stack spacing='xs'>
          <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
            When changes apply
          </Text>
          <Text size='sm'>{whenChangesApply}</Text>
        </Stack>
      </Paper>

      <Paper p='md' radius='md' withBorder>
        <Stack spacing='xs'>
          <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
            Package
          </Text>
          <ReviewRow label='Current' value={athletePackage.package.name} />
          <Divider variant='dashed' />
          <ReviewRow label='New package' value={captured?.package.name ?? '—'} />
          <ReviewRow
            label='Status upon start'
            value={captured?.athleteStatusOnStart ?? 'No Status Change'}
          />
          <ReviewRow label='Billing plan' value={captured?.billingPlan.name ?? '—'} />
          <ReviewRow
            label='Payment method'
            value={
              captured?.nonStripePaymentMethod ??
              (captured?.paymentMethodId ? <Badge size='xs'>set</Badge> : '—')
            }
          />
          <ReviewRow label='Auto-pay' value={captured?.autoPay ? 'On' : 'Off'} />
          <ReviewRow
            label='Auto-renew'
            value={
              effectiveAutoRenew ? (
                'On'
              ) : (
                <Text size='sm' c='dimmed'>
                  {captured?.followUpPackageId ? 'Off (follow-up package set)' : 'Off'}
                </Text>
              )
            }
          />
          {captured?.discount && (
            <ReviewRow
              label='Discount'
              value={
                <Text size='sm' c='green' fw={600}>
                  {captured.discount.name} {formatDiscountValue(captured.discount, formatCurrency, false)}
                  {captured.applyDiscountToAll ? ' (all installments)' : ''}
                </Text>
              }
            />
          )}
        </Stack>
      </Paper>

      {upcomingTotalCount > 0 && (
        <Paper p='md' radius='md' withBorder>
          <Stack spacing='xs'>
            <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
              Future reservations
            </Text>
            <ReviewRow
              label='Action'
              value={
                willKeep ? (
                  <Badge color='yellow' size='sm'>
                    Keep & re-credit on activation
                  </Badge>
                ) : (
                  <Badge color='red' size='sm'>
                    Delete
                  </Badge>
                )
              }
            />
            <ReviewRow label='Credited' value={upcomingCreditedCount} />
            <ReviewRow label='Uncredited' value={upcomingUncreditedCount} />
          </Stack>
        </Paper>
      )}

      <Paper p='md' radius='md' withBorder>
        <Stack spacing='xs'>
          <Text size='xs' fw={600} tt='uppercase' c='dimmed'>
            Credits and billing
          </Text>
          {timing === 'TOMORROW' && (
            <Text size='sm'>
              Future credit dispensations and rollover credits on the current package will be zeroed. Future
              invoices on the current package will be voided.
            </Text>
          )}
          {timing === 'WHEN_ENDS' && willKeep && upcomingTotalCount > 0 && (
            <Text size='sm'>
              Kept reservations will be re-credited to the new package automatically when it activates.
            </Text>
          )}
          {timing === 'WHEN_ENDS' && !willKeep && (
            <Text size='sm'>The current package&apos;s credits and billing schedule are not modified.</Text>
          )}
        </Stack>
      </Paper>

      {hasReservationWarnings && (
        <Paper p='md' radius='md' withBorder>
          <Group align='flex-start' spacing='xs' noWrap>
            <ThemeIcon color='yellow' variant='light' radius='xl' size='lg'>
              <IconAlertTriangle size={16} />
            </ThemeIcon>
            <Stack spacing={2}>
              {willDelete && (
                <Text size='sm'>{upcomingTotalCount} reservation(s) will be permanently deleted.</Text>
              )}
              {willKeep && upcomingCreditedCount > 0 && (
                <Text size='sm'>
                  {upcomingCreditedCount} credited reservation(s) will be temporarily uncredited and
                  re-credited when the new package activates. Coverage by the new package&apos;s credits is
                  not guaranteed.
                </Text>
              )}
            </Stack>
          </Group>
        </Paper>
      )}

      {existingChild && (
        <KiloAlert variantStyle='warning'>
          <Box>
            <Text size='sm' fw={600}>
              Replaces the existing {existingChildKind}.
            </Text>
            <Text size='sm'>
              The current {existingChildKind} ({existingChild.packageName}, starts{' '}
              {dayjs(existingChild.startDate).format('MMM D, YYYY')}) will be removed and replaced by this
              new package.
            </Text>
          </Box>
        </KiloAlert>
      )}

      <KiloAlert variantStyle='error'>
        <Box>
          <Text size='sm' fw={600}>
            This action cannot be undone.
          </Text>
          <Text size='sm'>This will affect the athlete&apos;s active package.</Text>
        </Box>
      </KiloAlert>

      {errorMessage && (
        <KiloAlert variantStyle='error'>
          <Text size='sm'>{errorMessage}</Text>
        </KiloAlert>
      )}
    </Stack>
  );
};

export default ReviewStep;
